import { RushHour } from '@minigame-race/contracts';
import { useRef, useState } from 'react';
import { color } from '../../theme.js';
import type { MinigameProps } from '../../race/types.js';

/**
 * Block Escape (Rush Hour). Drag a block along its axis to slide it; release to
 * commit the move. The block follows your finger during the drag, snapping to
 * whole cells, and the server validates the final slide. If the move is illegal
 * the server rejects it and the block snaps back to its authoritative position.
 */

const BLOCK_COLORS = ['#ffd23f', '#3fb0ff', '#a6ff2e', '#ff8f3f', '#c07dff', '#3fffd2', '#ff6ad5', '#7d9cff', '#ffe08a', '#89f0a0'];

function isView(v: unknown): v is RushHour.RushPrivateView {
  return typeof v === 'object' && v !== null && (v as { kind?: string }).kind === 'rush-hour';
}

interface Drag {
  carId: number;
  /** Offset in cells from the block's authoritative position (snapped, signed). */
  offset: number;
}

export function RushHourGame({ view, emit }: MinigameProps) {
  const s = isView(view) ? view : null;
  const boardRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ carId: number; startX: number; startY: number } | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  if (!s) return null;

  const n = s.size;

  const carById = (id: number): RushHour.RushCar | undefined => s.cars.find((c) => c.id === id);

  /** Occupancy excluding the dragged block, for clamping the drag to legal cells. */
  const cellFree = (carId: number, r: number, c: number): boolean => {
    if (r < 0 || c < 0 || r >= n || c >= n) return false;
    for (const car of s.cars) {
      if (car.id === carId) continue;
      for (let k = 0; k < car.length; k++) {
        const cr = car.horizontal ? car.row : car.row + k;
        const cc = car.horizontal ? car.col + k : car.col;
        if (cr === r && cc === c) return false;
      }
    }
    return true;
  };

  /** Largest legal |offset| the block can slide in each direction from its origin. */
  const clampOffset = (car: RushHour.RushCar, raw: number): number => {
    const dir = Math.sign(raw);
    if (dir === 0) return 0;
    let ok = 0;
    for (let step = 1; step <= Math.abs(raw); step++) {
      // The new leading cell entered at this step.
      const lead = dir > 0 ? car.length - 1 + step : -step;
      const r = car.horizontal ? car.row : car.row + lead;
      const c = car.horizontal ? car.col + lead : car.col;
      if (cellFree(car.id, r, c)) ok = step * dir;
      else break;
    }
    return ok;
  };

  /** Pixels per cell — blocks and grid share the full board box. */
  const cellPx = (): number => (boardRef.current ? boardRef.current.clientWidth / n : 1);

  const onDown = (e: React.PointerEvent, carId: number): void => {
    if (s.isComplete) return;
    boardRef.current?.setPointerCapture(e.pointerId);
    dragRef.current = { carId, startX: e.clientX, startY: e.clientY };
    setDrag({ carId, offset: 0 });
  };

  const onMove = (e: React.PointerEvent): void => {
    const d = dragRef.current;
    if (!d) return;
    const car = carById(d.carId);
    if (!car) return;
    const px = cellPx();
    const rawCells = car.horizontal ? (e.clientX - d.startX) / px : (e.clientY - d.startY) / px;
    const snapped = Math.round(rawCells);
    const clamped = clampOffset(car, snapped);
    setDrag((cur) => (cur && cur.offset === clamped ? cur : { carId: d.carId, offset: clamped }));
  };

  const onUp = (e: React.PointerEvent): void => {
    const d = dragRef.current;
    dragRef.current = null;
    boardRef.current?.releasePointerCapture?.(e.pointerId);
    const finalOffset = drag?.offset ?? 0;
    setDrag(null);
    if (d && finalOffset !== 0) emit({ type: 'move', car: d.carId, delta: finalOffset });
  };

  return (
    <div style={{ width: '100%', maxWidth: 'min(88cqmin,420px)', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'clamp(10px,2.2cqmin,16px)' }}>
      <div style={{ textAlign: 'center', fontFamily: "'JetBrains Mono',monospace", fontSize: 'clamp(11px,2.2cqmin,15px)' }}>
        <span style={{ color: s.moves > s.par ? color.amberBright : color.lime, fontWeight: 700 }}>{s.moves}</span>
        <span style={{ opacity: 0.5 }}> moves · par {s.par}</span>
      </div>

      <div style={{ position: 'relative', width: '100%', aspectRatio: '1 / 1' }}>
        {/* exit marker on the target row */}
        <div
          aria-hidden
          style={{
            position: 'absolute', right: -3, top: `${(s.exitRow / n) * 100}%`,
            height: `${(1 / n) * 100}%`, width: 6, borderRadius: 3,
            background: '#ff4d4d', boxShadow: '0 0 10px #ff4d4d',
          }}
        />
        {/* grid + blocks live in one padded square; blocks are absolutely placed */}
        <div
          ref={boardRef}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          style={{
            position: 'absolute', inset: 0, borderRadius: 12,
            background: 'rgba(0,0,0,.4)', border: '1px solid rgba(166,255,46,.14)',
            touchAction: 'none', userSelect: 'none',
          }}
        >
          {/* faint grid cells — same box as the blocks, so coordinates align */}
          <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: `repeat(${n},1fr)`, gridTemplateRows: `repeat(${n},1fr)` }} aria-hidden>
            {Array.from({ length: n * n }, (_, i) => (
              <div key={i} style={{ border: '1px solid rgba(255,255,255,.03)', borderRadius: 4 }} />
            ))}
          </div>

          {/* blocks */}
          {s.cars.map((car) => {
            const dOff = drag && drag.carId === car.id ? drag.offset : 0;
            const row = car.horizontal ? car.row : car.row + dOff;
            const col = car.horizontal ? car.col + dOff : car.col;
            const w = car.horizontal ? car.length : 1;
            const h = car.horizontal ? 1 : car.length;
            const isDragging = drag?.carId === car.id;
            return (
              <div
                key={car.id}
                onPointerDown={(e) => onDown(e, car.id)}
                style={{
                  position: 'absolute',
                  left: `${(col / n) * 100}%`,
                  top: `${(row / n) * 100}%`,
                  width: `${(w / n) * 100}%`,
                  height: `${(h / n) * 100}%`,
                  padding: 'clamp(2px,0.7cqmin,5px)',
                  boxSizing: 'border-box',
                  cursor: car.horizontal ? 'ew-resize' : 'ns-resize',
                  touchAction: 'none',
                  transition: isDragging ? 'none' : 'left .12s ease, top .12s ease',
                  zIndex: isDragging ? 3 : 2,
                }}
              >
                <div
                  style={{
                    width: '100%', height: '100%', borderRadius: 7,
                    background: car.target ? '#ff4d4d' : BLOCK_COLORS[car.id % BLOCK_COLORS.length],
                    border: '1px solid rgba(0,0,0,.4)',
                    boxShadow: car.target
                      ? `0 0 10px rgba(255,77,77,.6)${isDragging ? ', 0 0 0 2px #fff' : ''}`
                      : isDragging ? '0 0 0 2px #fff' : '0 2px 6px rgba(0,0,0,.35)',
                    display: 'grid', placeItems: 'center',
                  }}
                >
                  {car.target && (
                    <span style={{ fontSize: 'clamp(10px,2.4cqmin,16px)', fontWeight: 800, color: '#2a0006', letterSpacing: '.08em' }}>OUT →</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ textAlign: 'center', fontSize: 'clamp(10px,1.9cqmin,13px)', color: 'rgba(234,255,234,.4)' }}>
        Drag a block along its lane. Get the red block to the exit.
      </div>
    </div>
  );
}
